import { create } from "zustand";
import axios from "axios";
import { toast } from "sonner";
import { calculateAge } from "../hooks/useServiceCalls";
import { isAssignableUser } from "../lib/utils";
import { copyText } from "../lib/clipboard";
import BACKEND_URL from "../lib/apiConfig";
import { axiosWithRetry } from "../lib/axiosRetry";
import { describeSync } from "../lib/syncToast";
import { beginMutation, endMutation, cancelMutation, isGuarded, guardRows } from "../lib/mutationGuard";
import { createFiltersSlice } from "./serviceCalls/filtersSlice";
import { createPinsAttentionSlice } from "./serviceCalls/pinsAttentionSlice";
import { SERVICE_STATUSES, DEFAULT_SERVICE_STATUSES } from "../lib/serviceCallStatuses";
import { isShopCall, saveShopOrder, shopCallPatch, shopError } from "../components/service-shop-orders/shopOrderApi";

const API = `${BACKEND_URL}/api`;

// Tiny safe-localStorage-JSON helper, also used inside filtersSlice.js.
// Kept in both files (it's 8 lines) rather than introducing a shared
// "storeUtils" module that would only host one function.
const loadPersisted = (key, fallback) => {
  try {
    const saved = localStorage.getItem(key);
    if (saved === null) return fallback;
    return JSON.parse(saved);
  } catch {
    return fallback;
  }
};

const DEFAULT_STATUSES = DEFAULT_SERVICE_STATUSES;
const AIRTABLE_STATUSES_ALL = SERVICE_STATUSES;

const useServiceCallsStore = create((set, get) => ({
  // ===== Data =====
  calls: [],
  customers: [],
  technicians: [],
  parts: [],
  kbArticles: [],
  allTags: [],
  tagsLoading: true,

  // ===== Slices: Filters + Pins/Attention (extracted iter 370bq) =====
  ...createFiltersSlice(set, get),
  ...createPinsAttentionSlice(set, get),

  // ===== Top-level state still owned by this file =====
  loading: true,
  syncing: false,
  lastSyncedAt: null,
  averageAge: 14,

  // ===== Dialog state =====
  selectedCall: null,
  pendingShopOrder: null,
  canDispatchShopOrders: false,
  editDialogOpen: false,
  tagManageOpen: false,
  paymentAmount: "",
  creatingPayment: false,

  // ===== Inline editing =====
  editingRowId: null,
  editingData: {},

  // ===== Current user =====
  currentUser: {
    id: localStorage.getItem("user_id") || "unknown",
    name: localStorage.getItem("user_name") || "Unknown User",
    role: localStorage.getItem("user_role") || "dispatcher",
  },

  // ===== Dialog setters =====
  setSelectedCall: (v) => set({ selectedCall: typeof v === "function" ? v(get().selectedCall) : v }),
  setEditDialogOpen: (v) => set({ editDialogOpen: v }),
  setTagManageOpen: (v) => set({ tagManageOpen: v }),
  setPaymentAmount: (v) => set({ paymentAmount: v }),
  setEditingData: (v) => set({ editingData: typeof v === "function" ? v(get().editingData) : v }),
  // iter 370by — close-out gate. When the user attempts to mark a
  // call Complete WITHOUT having tagged its outcome first, we stash
  // the pending status change in `pendingCloseOut` and the dispatch
  // page renders <RequireOutcomeBeforeCloseDialog/>. Once the user
  // picks a tag in that modal, we save the tag AND apply the
  // originally-requested status flip.
  pendingCloseOut: null, // { callId, newStatus, syncToAirtable, triggerCelebration } | null
  setPendingCloseOut: (v) => set({ pendingCloseOut: v }),
  // iter 379l — Jake: "if we change a date or tech, it should prompt us
  // to reset the tech status." Set after a date/tech change lands on a
  // call that's marked completed/non_complete; the dispatch page renders
  // TechStatusResetPromptDialog off this. { callId, statusLabel, trigger }
  pendingTechStatusReset: null,
  setPendingTechStatusReset: (v) => set({ pendingTechStatusReset: v }),
  maybePromptTechStatusReset: (callId, trigger) => {
    const call = get().calls.find((c) => c.id === callId || c.airtable_id === callId);
    const st = (call?.status || "").toLowerCase();
    if (st !== "completed" && st !== "non_complete") return;
    set({
      pendingTechStatusReset: {
        callId: call.id,
        statusLabel: st === "completed" ? "Complete" : "Non-Complete",
        trigger,
      },
    });
  },

  // ===== Data fetching =====
  fetchData: async () => {
    let loadingWatchdog = null;
    try {
      // STALE-WHILE-REVALIDATE: Show cached data instantly, refresh in background
      const cacheVersion = loadPersisted("sc_cache_version", null);
      const cached = cacheVersion === 2 ? loadPersisted("sc_cache_calls", null) : null;
      const cachedUsers = cacheVersion === 2 ? loadPersisted("sc_cache_users", null) : null;
      if (!cacheVersion || cacheVersion !== 2) {
        // Invalidate old cache
        try { localStorage.removeItem("sc_cache_calls"); localStorage.removeItem("sc_cache_users"); } catch {}
      }
      if (cached && Array.isArray(cached) && cached.length > 0) {
        // Show stale data immediately — no loading spinner
        const cachedTechs = cachedUsers && Array.isArray(cachedUsers) && cachedUsers.length > 0
          ? { technicians: cachedUsers.filter((u) => isAssignableUser(u)) }
          : {};
        set({
          calls: cached,
          loading: false,
          ...cachedTechs,
        });
      } else {
        set({ loading: true });
      }

      // Never keep UI in spinner forever on cold starts / weak network
      loadingWatchdog = setTimeout(() => {
        set({ loading: false });
      }, 12000);

      // Fetch fresh data in background — service calls first (most important)
      const callsPromise = axiosWithRetry(
        () => axios.get(`${API}/service-calls`, { timeout: 12000 }),
        { retries: 1, delay: 800, maxDelay: 1500, silent: true }
      ).catch((e) => {
        console.error("Service calls fetch failed after retries:", e.message);
        return null;
      });

      // Fire secondary requests in parallel with retry for cold starts
      const secondaryPromise = Promise.all([
        axiosWithRetry(() => axios.get(`${API}/customers`, { timeout: 10000 })).catch(() => ({ data: [] })),
        axiosWithRetry(() => axios.get(`${API}/users`, { timeout: 10000 })).catch(() => ({ data: [] })),
        axiosWithRetry(() => axios.get(`${API}/parts`, { timeout: 10000 })).catch(() => ({ data: [] })),
        axios.get(`${API}/integrations/airtable/sync-status`, { timeout: 10000 }).catch(() => ({ data: {} })),
      ]);

      // Show service calls as soon as they arrive
      const callsRes = await callsPromise;
      if (callsRes && Array.isArray(callsRes.data) && callsRes.data.length > 0) {
        set({ calls: guardRows(callsRes.data, get().calls), loading: false });
        // Persist to localStorage for next stale-while-revalidate
        try { localStorage.setItem("sc_cache_calls", JSON.stringify(callsRes.data)); localStorage.setItem("sc_cache_version", "2"); } catch {}
      } else {
        set({ loading: false });
      }

      // Then update secondary data when it arrives
      const [customersRes, usersRes, partsRes, syncStatusRes] = await secondaryPromise;
      const freshUsers = usersRes.data || [];
      const freshTechs = freshUsers.filter((u) => isAssignableUser(u));
      const update = {
        customers: customersRes.data,
        // Only overwrite technicians if we got real data — don't clear with empty response
        ...(freshTechs.length > 0 ? { technicians: freshTechs } : {}),
        parts: partsRes.data,
        lastSyncedAt: syncStatusRes.data?.last_synced_at || null,
      };
      set(update);
      // Cache users for stale-while-revalidate (only if we got real data)
      if (freshUsers.length > 0) {
        try { localStorage.setItem("sc_cache_users", JSON.stringify(freshUsers)); } catch {}
      }
    } catch (err) {
      console.error("Failed to fetch data:", err);
      set({ loading: false });
    } finally {
      if (loadingWatchdog) clearTimeout(loadingWatchdog);
    }
  },

  // Background refresh — smart merge without loading flicker
  refreshCalls: async (includeInactive = false) => {
    try {
      const url = includeInactive ? `${API}/service-calls?include_inactive=true` : `${API}/service-calls`;
      const callsRes = await axios.get(url);
      const newCalls = callsRes.data;
      const oldCalls = get().calls;
      const oldById = new Map(oldCalls.map(c => [c.id, c]));
      const oldByAirtableId = new Map(oldCalls.filter(c => c.airtable_id).map(c => [c.airtable_id, c]));
      const merged = newCalls.map(nc => {
        const oc = oldById.get(nc.id) || (nc.airtable_id ? oldByAirtableId.get(nc.airtable_id) : null);
        if (!oc) return nc;
        // Preserve calls with in-flight optimistic updates — don't overwrite with stale API data
        if (oc._syncing || isGuarded(nc.id) || isGuarded(nc.airtable_id)) return oc;
        if (oc.customer_name === nc.customer_name &&
            oc.airtable_status === nc.airtable_status &&
            oc.status === nc.status &&
            oc.scheduled_date === nc.scheduled_date &&
            oc.date_locked === nc.date_locked &&
            oc.assigned_tech_name === nc.assigned_tech_name &&
            oc.assigned_tech_id === nc.assigned_tech_id &&
            oc.customer_address === nc.customer_address &&
            oc.customer_phone === nc.customer_phone &&
            oc.notes === nc.notes &&
            oc.internal_notes === nc.internal_notes &&
            oc.customer_says === nc.customer_says &&
            oc.no_parts_needed === nc.no_parts_needed &&
            oc.parts_needed === nc.parts_needed &&
            // iter 370bw — keep `outcome_tag` in the merge equality
            // check so a background refresh that just-barely missed the
            // pod-side cache invalidation doesn't blow away Jake's
            // freshly-saved tag.
            oc.outcome_tag === nc.outcome_tag &&
            oc.latest_note === nc.latest_note) {
          return oc;
        }
        return nc;
      });
      set({ calls: merged, lastSyncedAt: new Date().toISOString() });
      // Update cache
      try { localStorage.setItem("sc_cache_calls", JSON.stringify(merged)); } catch {}
    } catch {
      // silent — background refresh
    }
  },

  fetchKbArticles: async () => {
    try {
      const res = await axios.get(`${API}/knowledge`);
      set({ kbArticles: res.data });
    } catch (err) {
      console.error("Failed to fetch KB articles:", err);
    }
  },

  fetchTags: async () => {
    try {
      const res = await axios.get(`${API}/tags`);
      set({ allTags: res.data, tagsLoading: false });
    } catch (err) {
      console.error("Failed to fetch tags:", err);
      set({ tagsLoading: false });
    }
  },

  fetchAverageAge: async () => {
    try {
      const res = await axios.get(`${API}/analytics/average-age`);
      if (res.data?.average_age_days) set({ averageAge: Math.round(res.data.average_age_days) });
    } catch (err) {
      console.error("Failed to fetch average age:", err);
    }
  },

  // ===== Sync =====
  syncFromAirtable: async (silent = false, useIncremental = true) => {
    try {
      set({ syncing: true });
      const endpoint = useIncremental
        ? `${API}/integrations/airtable/sync-incremental${silent ? "" : "?force=1"}`
        : `${API}/integrations/airtable/sync-service-calls`;
      const res = await axios.post(endpoint);
      if (!silent) toast.success(describeSync(res.data));
      set({ lastSyncedAt: res.data.synced_at });
      get().refreshCalls();
    } catch (err) {
      if (!silent) toast.error("Failed to sync from Airtable");
    } finally {
      set({ syncing: false });
    }
  },

  // ===== Tag operations =====
  getTagColor: (tagName) => {
    const tag = get().allTags.find((t) => t.name === tagName);
    return tag?.color || "#6B7280";
  },

  addTagsToCall: async (callId, tagNames) => {
    const { calls } = get();
    const call = calls.find((c) => c.id === callId);
    if (!call || tagNames.length === 0) return;
    const newTags = [...new Set([...(call.tags || []), ...tagNames])];
    // iter 370bw — `_syncing: true` protects the optimistic patch from
    // being overwritten by a background `refreshCalls()` that hits a
    // sibling backend pod with stale `sc:active_calls` cache (TTL=120s).
    // Without it, a refresh that fires before the PATCH replicates can
    // revert the tags Jake just added — same multi-pod race we fixed on
    // inventory in iter 370bt/370bu/370bv.
    set({ calls: calls.map((c) => (c.id === callId ? { ...c, tags: newTags, _syncing: true } : c)) });
    try {
      await axios.patch(`${API}/service-calls/${callId}`, { tags: newTags });
      set({ calls: get().calls.map((c) => (c.id === callId ? { ...c, _syncing: false } : c)) });
      toast.success(tagNames.length === 1 ? "Tag added" : `${tagNames.length} tags added`);
    } catch {
      toast.error("Failed to add tags");
      get().refreshCalls();
    }
  },

  removeTagFromCall: async (callId, tagName) => {
    const { calls } = get();
    const call = calls.find((c) => c.id === callId);
    if (!call) return;
    const newTags = (call.tags || []).filter((t) => t !== tagName);
    set({ calls: calls.map((c) => (c.id === callId ? { ...c, tags: newTags, _syncing: true } : c)) });
    try {
      await axios.patch(`${API}/service-calls/${callId}`, { tags: newTags });
      set({ calls: get().calls.map((c) => (c.id === callId ? { ...c, _syncing: false } : c)) });
      toast.success("Tag removed");
    } catch {
      toast.error("Failed to remove tag");
      get().refreshCalls();
    }
  },

  createTag: async (name, color) => {
    const trimmedName = name.trim();
    if (!trimmedName) return null;
    try {
      const res = await axios.post(`${API}/tags`, { name: trimmedName, color });
      set({ allTags: [...get().allTags, res.data] });
      toast.success(`Tag "${trimmedName}" created`);
      return res.data;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create tag");
      return null;
    }
  },

  updateTag: async (tagId, updates) => {
    try {
      const res = await axios.patch(`${API}/tags/${tagId}`, updates);
      set({ allTags: get().allTags.map((t) => (t.id === tagId ? res.data : t)) });
      toast.success("Tag updated");
      return res.data;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update tag");
      return null;
    }
  },

  deleteTag: async (tagId) => {
    const { allTags, calls, tagFilter } = get();
    const tag = allTags.find((t) => t.id === tagId);
    if (!tag || tag.is_default) {
      if (tag?.is_default) toast.error("Cannot delete default tags");
      return;
    }
    try {
      await axios.delete(`${API}/tags/${tagId}`);
      set({
        allTags: allTags.filter((t) => t.id !== tagId),
        tagFilter: tagFilter.filter((t) => t !== tag.name),
        calls: calls.map((c) => ({ ...c, tags: (c.tags || []).filter((t) => t !== tag.name) })),
      });
      toast.success(`Tag "${tag.name}" deleted`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete tag");
    }
  },

  // ===== KB operations =====
  handlePinKbArticle: async (callId, articleId) => {
    set({ calls: get().calls.map((c) => (c.id === callId ? { ...c, pinned_kb_article_id: articleId, _syncing: true } : c)) });
    try {
      await axios.patch(`${API}/service-calls/${callId}`, { pinned_kb_article_id: articleId });
      set({ calls: get().calls.map((c) => (c.id === callId ? { ...c, _syncing: false } : c)) });
      toast.success(articleId ? "Article pinned to ticket" : "Article unpinned");
    } catch {
      toast.error("Failed to update article");
      get().refreshCalls();
    }
  },

  handleCreateKbArticle: async (articleData, callIdToPin) => {
    if (!articleData.title || !articleData.content) {
      toast.error("Title and content are required");
      return;
    }
    try {
      const res = await axios.post(`${API}/knowledge`, articleData);
      set({ kbArticles: [...get().kbArticles, res.data] });
      toast.success("Knowledge base article created");
      if (callIdToPin) await get().handlePinKbArticle(callIdToPin, res.data.id);
    } catch {
      toast.error("Failed to create article");
    }
  },

  handleUpdateKbArticle: async (articleData) => {
    if (!articleData.id || !articleData.title || !articleData.content) {
      toast.error("Title and content are required");
      return;
    }
    try {
      await axios.put(`${API}/knowledge/${articleData.id}`, articleData);
      set({ kbArticles: get().kbArticles.map((a) => (a.id === articleData.id ? articleData : a)) });
      toast.success("Knowledge base article updated");
    } catch {
      toast.error("Failed to update article");
    }
  },

  // ===== Field handlers =====
  handleAddressEdit: async (callId, newAddress, oldAddress = "") => {
    set({ calls: get().calls.map((c) => (c.id === callId ? { ...c, customer_address: newAddress, _syncing: true } : c)) });
    try {
      const res = await axios.patch(`${API}/service-calls/${callId}`, { customer_address: newAddress });
      set({ calls: get().calls.map((c) => (c.id === callId ? { ...c, customer_address: newAddress, _syncing: false } : c)) });
      // iter 371cn — Also log the change to the note_log so future
      // dispatchers know the service address was corrected from the
      // Airtable/invoice delivery address (Jake: "James Nguyen's
      // address on file was the campground delivery address, need
      // the actual service address"). Best-effort — a note-log
      // failure shouldn't roll back the address save.
      try {
        const currentUser = get().currentUser || {};
        const beforeText = oldAddress?.trim() ? `was "${oldAddress.trim()}"` : "was blank";
        await axios.post(`${API}/note-logs`, {
          service_call_id: callId,
          user_id: currentUser.id || "system",
          user_name: currentUser.name || "Dispatcher",
          note: `Service address updated — ${beforeText}, now "${newAddress}".`,
          note_type: "dispatch",
        });
      } catch (logErr) {
        // Silent — the address save already succeeded.
        console.debug("Address change note-log failed:", logErr);
      }
      toast.success(res.data.airtable_push_result === "success" ? "Address saved & synced to Airtable" : "Address saved");
    } catch {
      toast.error("Failed to update address");
      get().refreshCalls();
    }
  },

  handlePartsUpdate: async (callId, partsNeeded) => {
    set({ calls: get().calls.map((c) => (c.id === callId ? { ...c, parts_needed: partsNeeded, _syncing: true } : c)) });
    try {
      await axios.patch(`${API}/service-calls/${callId}`, { parts_needed: partsNeeded });
      set({ calls: get().calls.map((c) => (c.id === callId ? { ...c, _syncing: false } : c)) });
      toast.success("Parts list saved");
    } catch {
      toast.error("Failed to save parts list");
      get().refreshCalls();
    }
  },

  handleNoPartsNeededToggle: async (callId, noPartsNeeded) => {
    const { selectedCall } = get();
    set({
      calls: get().calls.map((c) => (c.id === callId ? { ...c, no_parts_needed: noPartsNeeded, _syncing: true } : c)),
      selectedCall: selectedCall?.id === callId ? { ...selectedCall, no_parts_needed: noPartsNeeded } : selectedCall,
    });
    try {
      await axios.patch(`${API}/service-calls/${callId}`, { no_parts_needed: noPartsNeeded });
      set({ calls: get().calls.map((c) => (c.id === callId ? { ...c, _syncing: false } : c)) });
      toast.success(noPartsNeeded ? "Marked as no parts needed" : "Parts requirement restored");
    } catch {
      toast.error("Failed to update");
      get().refreshCalls();
    }
  },

  handleCustomerSaysUpdate: async (callId, customerSays) => {
    set({ calls: get().calls.map((c) => (c.id === callId ? { ...c, customer_says: customerSays, _syncing: true } : c)) });
    try {
      await axios.patch(`${API}/service-calls/${callId}`, { customer_says: customerSays });
      set({ calls: get().calls.map((c) => (c.id === callId ? { ...c, _syncing: false } : c)) });
      toast.success("Customer says saved");
    } catch {
      toast.error("Failed to save customer says");
      get().refreshCalls();
    }
  },

  handleInternalNotesUpdate: async (callId, internalNotes) => {
    set({ calls: get().calls.map((c) => (c.id === callId ? { ...c, internal_notes: internalNotes, notes: internalNotes, latest_note: internalNotes, _syncing: true } : c)) });
    try {
      const response = await axios.patch(`${API}/service-calls/${callId}?sync_to_airtable=true`, { internal_notes: internalNotes, notes: internalNotes });
      set({ calls: get().calls.map((c) => (c.id === callId ? { ...response.data, _syncing: false } : c)) });
      const pushResult = response.data.airtable_push_result;
      toast.success(pushResult === "success" ? "Notes saved & synced to Airtable" : pushResult ? "Notes saved locally, Airtable sync failed" : "Notes saved");
    } catch {
      toast.error("Failed to save notes");
      get().refreshCalls();
    }
  },

  // iter 370dw — Payment Notes column. Same pattern as internal notes:
  // optimistic local update, PATCH the call with `payment_notes`, the
  // backend mirrors to Airtable "Payment Notes" field on push.
  handlePaymentNotesUpdate: async (callId, paymentNotes) => {
    set({ calls: get().calls.map((c) => (c.id === callId ? { ...c, payment_notes: paymentNotes, _syncing: true } : c)) });
    try {
      const response = await axios.patch(`${API}/service-calls/${callId}?sync_to_airtable=true`, { payment_notes: paymentNotes });
      set({ calls: get().calls.map((c) => (c.id === callId ? { ...response.data, _syncing: false } : c)) });
      toast.success("Payment notes saved");
    } catch {
      toast.error("Failed to save payment notes");
      get().refreshCalls();
    }
  },

  // iter 370dx — Parts Notes textarea in the expanded ticket modal.
  // Mongo-only for now (no Airtable column) so we skip the sync hop.
  handlePartsNotesUpdate: async (callId, partsNotes) => {
    set({ calls: get().calls.map((c) => (c.id === callId ? { ...c, parts_notes: partsNotes, _syncing: true } : c)) });
    try {
      const response = await axios.patch(`${API}/service-calls/${callId}?sync_to_airtable=false`, { parts_notes: partsNotes });
      set({ calls: get().calls.map((c) => (c.id === callId ? { ...response.data, _syncing: false } : c)) });
      toast.success("Parts notes saved");
    } catch {
      toast.error("Failed to save parts notes");
      get().refreshCalls();
    }
  },

  // iter 370dw — Waiting-on-Parts toggle. Calls the dedicated
  // /waiting-on-parts endpoint which (a) flips the boolean, (b)
  // optionally stores a parts_eta, (c) sets paused_at/paused_until
  // so age math freezes.
  handleToggleWaitingOnParts: async (callId, partsEta = null) => {
    const nowIso = new Date().toISOString();
    set({ calls: get().calls.map((c) => (c.id === callId ? { ...c, waiting_on_parts: true, parts_eta: partsEta, paused_at: nowIso, paused_until: partsEta || null, _syncing: true } : c)) });
    try {
      await axios.post(`${API}/service-calls/${callId}/waiting-on-parts`, { parts_eta: partsEta });
      set({ calls: get().calls.map((c) => (c.id === callId ? { ...c, _syncing: false } : c)) });
      toast.success(partsEta ? `Marked waiting on parts (ETA ${partsEta})` : "Marked waiting on parts");
    } catch {
      toast.error("Failed to mark waiting on parts");
      get().refreshCalls();
    }
  },

  handleClearWaitingOnParts: async (callId) => {
    set({ calls: get().calls.map((c) => (c.id === callId ? { ...c, waiting_on_parts: false, parts_eta: null, paused_at: null, paused_until: null, _syncing: true } : c)) });
    try {
      await axios.post(`${API}/service-calls/${callId}/clear-waiting-on-parts`);
      set({ calls: get().calls.map((c) => (c.id === callId ? { ...c, _syncing: false } : c)) });
      toast.success("Parts received — back in queue");
    } catch {
      toast.error("Failed to clear waiting on parts");
      get().refreshCalls();
    }
  },

  handlePhoneUpdate: async (callId, phone) => {
    set({ calls: get().calls.map((c) => (c.id === callId ? { ...c, customer_phone: phone, _syncing: true } : c)) });
    try {
      const response = await axios.patch(`${API}/service-calls/${callId}?sync_to_airtable=true`, { customer_phone: phone });
      set({ calls: get().calls.map((c) => (c.id === callId ? { ...response.data, _syncing: false } : c)) });
      toast.success(response.data.airtable_push_result === "success" ? "Phone updated & synced to Airtable" : "Phone number updated");
    } catch {
      toast.error("Failed to save phone number");
      get().refreshCalls();
    }
  },

  handleMakeUpdate: async (callId, yearMakeModel) => {
    set({ calls: get().calls.map((c) => (c.id === callId ? { ...c, year_make_model: yearMakeModel, _syncing: true } : c)) });
    try {
      await axios.patch(`${API}/service-calls/${callId}`, { year_make_model: yearMakeModel });
      set({ calls: get().calls.map((c) => (c.id === callId ? { ...c, _syncing: false } : c)) });
      toast.success("Make/model saved");
    } catch {
      toast.error("Failed to save make/model");
      get().refreshCalls();
    }
  },

  handleBillingUpdate: async (callId, field, value) => {
    set({ calls: get().calls.map((c) => (c.id === callId ? { ...c, [field]: value, _syncing: true } : c)) });
    try {
      await axios.patch(`${API}/service-calls/${callId}`, { [field]: value });
      set({ calls: get().calls.map((c) => (c.id === callId ? { ...c, _syncing: false } : c)) });
      toast.success("Billing info saved");
    } catch {
      toast.error("Failed to save billing info");
      get().refreshCalls();
    }
  },

  // iter 370bw — set / clear the outcome tag (replacement for the old
  // Prevention & Preparation review flow). One of:
  //   avoidable_inventory | avoidable_shop | making_money | other
  // `tag = null` clears the tag. Optimistically patches the local
  // store with `_syncing: true` so the value sticks across multi-pod
  // refreshes (same pattern as iter 370bv field handlers).
  setOutcomeTag: async (callId, tag) => {
    const userName = localStorage.getItem("user_name") || "Admin";
    const nowIso = new Date().toISOString();
    set({
      calls: get().calls.map((c) =>
        c.id === callId
          ? { ...c, outcome_tag: tag, outcome_tag_marked_by: tag ? userName : null, outcome_tag_marked_at: tag ? nowIso : null, _syncing: true }
          : c,
      ),
    });
    try {
      await axios.post(`${API}/service-calls/${callId}/outcome-tag`, { tag });
      set({ calls: get().calls.map((c) => (c.id === callId ? { ...c, _syncing: false } : c)) });
      // Quiet success — the picker visually flips on click, no need to
      // shout. (Opposite philosophy to status changes which DO toast
      // because those have downstream Airtable effects.)
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save tag");
      get().refreshCalls();
    }
  },

  // iter 370by — finishes a close-out attempt that was blocked because
  // the call wasn't categorized. Saves the tag first (so the API +
  // Airtable have the new category before the close-out hits Airtable
  // and triggers any outbound automations), then resumes the original
  // status update.
  confirmCloseOutWithTag: async (tag) => {
    const pending = get().pendingCloseOut;
    if (!pending || !tag) return;
    const { callId, newStatus, syncToAirtable, triggerCelebration } = pending;
    set({ pendingCloseOut: null });
    await get().setOutcomeTag(callId, tag);
    await get().handleQuickStatusUpdate(callId, newStatus, syncToAirtable, triggerCelebration);
  },

  handleScheduledDateUpdate: async (callId, newDate) => {
    const call = get().calls.find((c) => c.id === callId);
    if (call?.date_locked) {
      toast.error("Date is locked. Unlock it first to edit.");
      return;
    }
    const oldDate = call?.scheduled_date || '';
    const oldTentative = !!call?.date_tentative;
    set({ calls: get().calls.map((c) => (c.id === callId ? { ...c, scheduled_date: newDate, date_tentative: false, _syncing: true } : c)) });
    try {
      await axiosWithRetry(() => axios.patch(`${API}/service-calls/${callId}`, { scheduled_date: newDate }), { retries: 2, delay: 500, maxDelay: 1000, silent: true });
      set({ calls: get().calls.map((c) => (c.id === callId ? { ...c, _syncing: false } : c)) });
      // iter 379l — a date change on a completed/non-complete call
      // prompts a status reset (the new date stays either way).
      if (newDate !== oldDate) get().maybePromptTechStatusReset(callId, "date");
      // Log schedule changes to note_logs
      const userName = localStorage.getItem("user_name") || "System";
      const userId = localStorage.getItem("user_id") || "system";
      const scId = call?.airtable_id || callId;
      if (oldDate && !newDate) {
        axios.post(`${API}/note-logs`, { service_call_id: scId, user_id: userId, user_name: userName, note: `Schedule removed (was ${oldDate})`, note_type: "schedule" }).catch(() => {});
      } else if (newDate && newDate !== oldDate) {
        const msg = oldDate ? `Rescheduled from ${oldDate} to ${newDate}` : `Scheduled for ${newDate}`;
        axios.post(`${API}/note-logs`, { service_call_id: scId, user_id: userId, user_name: userName, note: msg, note_type: "schedule" }).catch(() => {});
      }
    } catch {
      // Revert optimistic update on failure
      set({ calls: get().calls.map((c) => (c.id === callId ? { ...c, scheduled_date: oldDate, date_tentative: oldTentative, _syncing: false } : c)) });
      toast.error("Failed to save date — reverted");
    }
  },

  toggleDateLock: async (callId) => {
    const isSameCall = (c) => c.id === callId || c.airtable_id === callId;
    const call = get().calls.find(isSameCall);
    if (!call) return;
    const newLockedState = !call.date_locked;
    set({
      calls: get().calls.map((c) => (isSameCall(c) ? { ...c, date_locked: newLockedState } : c)),
      selectedCall: get().selectedCall && isSameCall(get().selectedCall)
        ? { ...get().selectedCall, date_locked: newLockedState }
        : get().selectedCall,
    });

    const patchLock = (id) => axiosWithRetry(
      () => axios.patch(`${API}/service-calls/${id}`, { date_locked: newLockedState }, { timeout: 15000 }),
      { retries: 4, delay: 1000, maxDelay: 2500, silent: true }
    );

    const verifyPersisted = async () => {
      const lookupIds = [callId, call?.airtable_id, call?.id].filter(Boolean);
      for (const id of lookupIds) {
        try {
          const res = await axios.get(`${API}/service-calls/${id}`, { timeout: 10000 });
          if (Boolean(res.data?.date_locked) === Boolean(newLockedState)) return true;
        } catch {
          // try next id
        }
      }
      return false;
    };

    try {
      try {
        await patchLock(callId);
      } catch (primaryErr) {
        if (call?.airtable_id && call.airtable_id !== callId) {
          await patchLock(call.airtable_id);
        } else {
          throw primaryErr;
        }
      }
    } catch (err) {
      const persisted = await verifyPersisted();
      if (persisted) return;

      toast.error(`Failed to update lock state${err?.response?.status ? ` (${err.response.status})` : ""}`);
      set({
        calls: get().calls.map((c) => (isSameCall(c) ? { ...c, date_locked: !newLockedState } : c)),
        selectedCall: get().selectedCall && isSameCall(get().selectedCall)
          ? { ...get().selectedCall, date_locked: !newLockedState }
          : get().selectedCall,
      });
    }
  },

  toggleDateTentative: async (callId) => {
    const call = get().calls.find((c) => c.id === callId);
    if (!call) return;
    const newState = !call.date_tentative;
    set({ calls: get().calls.map((c) => (c.id === callId ? { ...c, date_tentative: newState } : c)) });
    try {
      await axiosWithRetry(() => axios.patch(`${API}/service-calls/${callId}`, { date_tentative: newState }), { retries: 2, delay: 1500, silent: true });
    } catch {
      set({ calls: get().calls.map((c) => (c.id === callId ? { ...c, date_tentative: !newState } : c)) });
      toast.error("Failed to update tentative status");
    }
  },

  handleQuickStatusUpdate: async (callId, newStatus, syncToAirtable = true, triggerCelebration = null) => {
    const { calls, averageAge } = get();
    const oldCall = calls.find((c) => c.id === callId);
    const oldStatus = oldCall?.airtable_status;
    if (newStatus === "In Shop") {
      if (!get().canDispatchShopOrders) { toast.error("Dispatcher access is required to assign shop work"); return; }
      try {
        const { data } = await saveShopOrder(callId, { status: "in_shop" });
        set(s => ({ calls: s.calls.map(c => c.id === callId ? { ...c, ...shopCallPatch(data) } : c), pendingShopOrder: { callId } }));
      } catch (e) { toast.error(shopError(e)); }
      return;
    }
    if (newStatus === "Shop Work Incomplete") {
      if (!get().canDispatchShopOrders) { toast.error("Dispatcher access is required"); return; }
      set({ pendingShopOrder: { callId, outcome: newStatus === "Shop Work Incomplete" ? "shop_work_incomplete" : "ready_to_redeliver" } });
      return;
    }
    // iter 370by — gate close-out on having an outcome tag set. Jake:
    // "before a service ticket is closed out, categorizing it is gonna
    // be required". If the user is flipping to Complete without a
    // tag, stash the request in `pendingCloseOut` and let the dispatch
    // page render the modal that asks for a tag first. The original
    // status change resumes after the user picks a tag inside the
    // modal (see `confirmCloseOutWithTag` below).
    if (
      newStatus === "Complete" &&
      oldCall &&
      !oldCall.outcome_tag &&
      oldCall.airtable_status !== "Complete"
    ) {
      set({
        pendingCloseOut: { callId, newStatus, syncToAirtable, triggerCelebration },
      });
      return;
    }
    set({ calls: calls.map((c) => (c.id === callId ? { ...c, airtable_status: newStatus, _pendingSync: true } : c)) });

    if (newStatus === "Complete" && oldCall?.airtable_status !== "Complete" && triggerCelebration) {
      const createdDate = oldCall?.created_date || oldCall?.created;
      let daysToComplete = 0;
      if (createdDate) {
        const created = new Date(createdDate + "T00:00:00");
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        daysToComplete = Math.max(0, Math.floor((today - created) / (1000 * 60 * 60 * 24)));
      }
      triggerCelebration({ daysToComplete, averageAge, customerName: oldCall?.customer_name || "Service Call", callId, call: oldCall });
    }
    const skipToast = newStatus === "Complete" && oldCall?.airtable_status !== "Complete" && triggerCelebration;

    try {
      const shouldSync = syncToAirtable || !!oldCall?.airtable_id;
      const res = await axiosWithRetry(() => axios.patch(`${API}/service-calls/${callId}?sync_to_airtable=${shouldSync}`, { airtable_status: newStatus }), { retries: 3, delay: 1500, silent: true });
      set({ calls: get().calls.map((c) => (c.id === callId ? { ...c, ...res.data, _pendingSync: false } : c)) });
      if (!skipToast) {
        toast.success(oldCall?.airtable_id ? "Status updated & synced" : "Status updated", {
          action: oldStatus ? {
            label: "Undo",
            onClick: () => get().handleQuickStatusUpdate(callId, oldStatus, syncToAirtable, null),
          } : undefined,
        });
      }
    } catch {
      toast.error("Failed to update status");
      set({ calls: get().calls.map((c) => (c.id === callId ? { ...c, airtable_status: oldCall?.airtable_status, _pendingSync: false } : c)) });
    }
  },

  handleQuickAssignUpdate: async (callId, techId, techName, { promptStatusReset = true } = {}) => {
    const { selectedCall, calls } = get();
    const oldCall = calls.find((c) => c.id === callId || c.airtable_id === callId);
    const oldTechId = oldCall?.assigned_tech_id;
    const oldTechName = oldCall?.assigned_tech_name;
    const desiredTechId = techId || null;
    const desiredTechName = techName || null;

    const updatePayload = {
      assigned_tech_id: desiredTechId,
      assigned_tech_name: desiredTechName,
    };

    const patchAssignment = (id) => axiosWithRetry(
      () => axios.patch(`${API}/service-calls/${id}`, updatePayload, { timeout: 15000 }),
      { retries: 4, delay: 1200, maxDelay: 2500, silent: true }
    );

    const verifyPersisted = async () => {
      const lookupIds = [callId, oldCall?.airtable_id].filter(Boolean);
      for (const id of lookupIds) {
        try {
          const res = await axios.get(`${API}/service-calls/${id}`, { timeout: 10000 });
          const persisted = res.data || {};
          if ((persisted.assigned_tech_id || null) === desiredTechId) {
            return true;
          }
        } catch {
          // try next lookup id
        }
      }
      return false;
    };

    set({
      calls: get().calls.map((c) => ((c.id === callId || c.airtable_id === callId) ? { ...c, assigned_tech_id: desiredTechId, assigned_tech_name: desiredTechName, _syncing: true } : c)),
      selectedCall: (selectedCall?.id === callId || selectedCall?.airtable_id === callId)
        ? { ...selectedCall, assigned_tech_id: desiredTechId, assigned_tech_name: desiredTechName }
        : selectedCall,
    });
    toast.success(techName ? `Assigned to ${techName}` : "Unassigned", {
      action: {
        label: "Undo",
        onClick: () => get().handleQuickAssignUpdate(callId, oldTechId || null, oldTechName || null, { promptStatusReset: false }),
      },
    });

    try {
      try {
        await patchAssignment(callId);
      } catch (primaryErr) {
        // Fallback to airtable_id when local id path fails (data shape inconsistencies / stale ids)
        if (oldCall?.airtable_id && oldCall.airtable_id !== callId) {
          await patchAssignment(oldCall.airtable_id);
        } else {
          throw primaryErr;
        }
      }

      set({
        calls: get().calls.map((c) => ((c.id === callId || c.airtable_id === callId) ? { ...c, _syncing: false } : c)),
      });
      // iter 379l — a tech change on a completed/non-complete call
      // prompts a status reset (the new tech stays either way).
      if (promptStatusReset && desiredTechId !== (oldTechId || null)) {
        get().maybePromptTechStatusReset(callId, "tech");
      }
    } catch (err) {
      // If request failed after server already saved the update, avoid false revert.
      const persisted = await verifyPersisted();
      if (persisted) {
        set({
          calls: get().calls.map((c) => ((c.id === callId || c.airtable_id === callId) ? { ...c, _syncing: false } : c)),
        });
        if (promptStatusReset && desiredTechId !== (oldTechId || null)) {
          get().maybePromptTechStatusReset(callId, "tech");
        }
        return;
      }

      // Revert optimistic update on failure
      set({
        calls: get().calls.map((c) => ((c.id === callId || c.airtable_id === callId) ? { ...c, assigned_tech_id: oldTechId || null, assigned_tech_name: oldTechName || null, _syncing: false } : c)),
        selectedCall: (selectedCall?.id === callId || selectedCall?.airtable_id === callId)
          ? { ...selectedCall, assigned_tech_id: oldTechId || null, assigned_tech_name: oldTechName || null }
          : selectedCall,
      });

      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      toast.error(`Failed to save assignment${status ? ` (${status})` : ""} — reverted${detail ? `: ${detail}` : ""}`);
    }
  },

  handleUncomplete: async (callId, { keepAssignment = false } = {}) => {
    const oldCall = get().calls.find(c => c.id === callId);
    beginMutation(callId);
    if (oldCall?.airtable_id) beginMutation(oldCall.airtable_id);
    // iter 379l — keepAssignment resets ONLY the tech status; the
    // assigned tech + scheduled date (and locks) stay untouched.
    const clearedFields = keepAssignment
      ? {}
      : { assigned_tech_id: null, assigned_tech_name: null, scheduled_date: null, date_locked: false, date_tentative: false };
    // Optimistic update immediately
    set({
      calls: get().calls.map((c) =>
        c.id === callId ? { ...c, status: "pending", completed_at: null, completed_by: null, non_complete_reason: null, non_complete_at: null, back_to_shop_at: null, back_to_shop_by: null, back_to_shop_notes: null, ...clearedFields, _syncing: true } : c
      ),
    });
    toast.success(keepAssignment ? "Tech status reset — tech & date kept" : "Tech status, assigned tech & date cleared");
    try {
      await axios.post(`${API}/tech/jobs/${callId}/uncomplete?tech_id=dispatcher&keep_assignment=${keepAssignment}`);
      endMutation(callId);
      if (oldCall?.airtable_id) endMutation(oldCall.airtable_id);
      set({ calls: get().calls.map((c) => (c.id === callId ? { ...c, _syncing: false } : c)) });
    } catch {
      cancelMutation(callId);
      if (oldCall?.airtable_id) cancelMutation(oldCall.airtable_id);
      // Revert on failure
      if (oldCall) {
        set({ calls: get().calls.map((c) => c.id === callId ? oldCall : c) });
      }
      toast.error("Failed to remove tech status");
    }
  },

  handleTechStatusChange: async (callId, newStatus, reason = "", triggerCelebration = null) => {
    const { calls, averageAge } = get();
    const currentCall = calls.find((c) => c.id === callId || c.airtable_id === callId);
    const userName = localStorage.getItem("user_name") || "Unknown User";
    // iter 398 — guard both ids so a poll/WS refetch can't snap the row
    // back to its old status while the server catches up.
    const guardIds = [callId, currentCall?.id, currentCall?.airtable_id].filter(Boolean);
    guardIds.forEach((id) => beginMutation(id));
    const matches = (c) => c.id === callId || c.airtable_id === callId;
    try {
      if (newStatus === "completed") {
        set({
          calls: get().calls.map((c) =>
            matches(c) ? { ...c, status: "completed", completed_at: new Date().toISOString(), completed_by: userName, _syncing: true } : c
          ),
        });
        await axios.post(`${API}/tech/jobs/${callId}/complete?tech_id=dispatcher`, { notes: "Marked complete by dispatcher" });
        const createdDate = currentCall?.created_date || currentCall?.created;
        let daysToComplete = 0;
        if (createdDate) {
          const created = new Date(createdDate + "T00:00:00");
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          daysToComplete = Math.max(0, Math.floor((today - created) / (1000 * 60 * 60 * 24)));
        }
        if (triggerCelebration) triggerCelebration({ daysToComplete, averageAge, customerName: currentCall?.customer_name || "Service Call", callId, call: currentCall });
        toast.success("Marked as completed", {
          action: {
            label: "Undo",
            onClick: () => get().handleUncomplete(callId),
          },
        });
      } else if (newStatus === "non_complete") {
        set({
          calls: get().calls.map((c) =>
            matches(c)
              ? { ...c, status: "non_complete", non_complete_reason: reason, non_complete_at: new Date().toISOString(), non_complete_by: userName, _syncing: true }
              : c
          ),
        });
        await axios.post(`${API}/tech/jobs/${callId}/non-complete?tech_id=dispatcher`, { reason: reason || "Marked non-complete by dispatcher" });
        toast.success("Marked as non-complete", {
          action: {
            label: "Undo",
            onClick: () => get().handleUncomplete(callId),
          },
        });
      }
      guardIds.forEach((id) => endMutation(id));
      set({ calls: get().calls.map((c) => (matches(c) ? { ...c, _syncing: false } : c)) });
    } catch {
      guardIds.forEach((id) => cancelMutation(id));
      if (currentCall) {
        set({ calls: get().calls.map((c) => (matches(c) ? currentCall : c)) });
      }
      toast.error("Failed to update tech status");
    }
  },

  handleAvoidabilityUpdate: (callId, updates) => {
    set({
      calls: get().calls.map((c) => (c.id === callId || c.airtable_id === callId ? { ...c, ...updates } : c)),
    });
  },

  handleProfitStatusUpdate: async (callId, newStatus) => {
    // Optimistic update
    beginMutation(callId);
    set({
      calls: get().calls.map((c) =>
        c.id === callId || c.airtable_id === callId ? { ...c, profit_status: newStatus, _syncing: true } : c
      ),
    });
    try {
      await axios.patch(`${API}/service-calls/${callId}?sync_to_airtable=false`, { profit_status: newStatus });
      endMutation(callId);
      set({ calls: get().calls.map((c) => (c.id === callId || c.airtable_id === callId ? { ...c, _syncing: false } : c)) });
    } catch {
      cancelMutation(callId);
      toast.error("Failed to update");
      get().refreshCalls();
    }
  },

  // ===== Inline editing =====
  startEditing: (call) => {
    set({
      editingRowId: call.id,
      editingData: {
        customer_name: call.customer_name || "",
        description: call.description || call.customer_says || "",
        airtable_status: call.airtable_status || call.status || "Standard Service",
        scheduled_date: call.scheduled_date || "",
        assigned_tech_id: call.assigned_tech_id || "",
        assigned_tech_name: call.assigned_tech_name || "",
      },
    });
  },

  cancelEditing: () => set({ editingRowId: null, editingData: {} }),

  saveEditing: async () => {
    const { editingRowId, editingData } = get();
    if (!editingRowId) return;
    try {
      await axios.patch(`${API}/service-calls/${editingRowId}`, editingData);
      toast.success("Changes saved");
      set({ editingRowId: null, editingData: {} });
      get().refreshCalls();
    } catch {
      toast.error("Failed to save changes");
    }
  },

  copyToClipboard: async (text, label = "Text") => {
    try {
      await copyText(text);
      toast.success(`${label} copied!`);
    } catch {
      toast.error("Failed to copy");
    }
  },

  // ===== Edit dialog =====
  handleSaveCallEdit: async () => {
    const { selectedCall } = get();
    if (!selectedCall) return;
    const updates = {
      airtable_status: selectedCall.airtable_status,
      description: selectedCall.description,
      notes: selectedCall.notes,
      assigned_tech_id: selectedCall.assigned_tech_id,
      assigned_tech_name: selectedCall.assigned_tech_name,
      parts: selectedCall.parts,
    };
    set({ calls: get().calls.map((c) => (c.id === selectedCall.id ? { ...c, ...updates } : c)) });
    toast.success("Service call updated");
    set({ editDialogOpen: false });
    try {
      await axios.patch(`${API}/service-calls/${selectedCall.id}`, updates);
    } catch {
      toast.error("Failed to save changes - please refresh");
    }
  },

  handleCreatePaymentLink: async () => {
    const { selectedCall, paymentAmount } = get();
    if (!selectedCall || !paymentAmount) {
      toast.error("Please enter an amount");
      return;
    }
    try {
      set({ creatingPayment: true });
      const res = await axios.post(`${API}/payments/send-payment-link`, {
        service_call_id: selectedCall.id || selectedCall.airtable_id,
        amount: parseFloat(paymentAmount),
        description: `Service call payment`,
        origin_url: window.location.origin,
        send_sms: false,
        send_email: false,
      });
      await copyText(res.data.payment_link);
      toast.success("Payment link copied to clipboard!");
      set({ paymentAmount: "" });
    } catch {
      toast.error("Failed to create payment link");
    } finally {
      set({ creatingPayment: false });
    }
  },

  pauseCall: async (callId, pausedUntil) => {
    try {
      await axios.post(`${API}/service-calls/${callId}/pause`, { paused_until: pausedUntil });
      set(state => ({
        calls: state.calls.map(c =>
          (c.id === callId || c.airtable_id === callId)
            ? { ...c, paused_until: pausedUntil, paused_at: new Date().toISOString() }
            : c
        ),
      }));
      toast.success("Call paused");
    } catch {
      toast.error("Failed to pause call");
    }
  },

  unpauseCall: async (callId) => {
    try {
      const res = await axios.post(`${API}/service-calls/${callId}/unpause`);
      set(state => ({
        calls: state.calls.map(c =>
          (c.id === callId || c.airtable_id === callId)
            ? { ...c, paused_until: null, paused_at: null, paused_days: res.data.paused_days }
            : c
        ),
      }));
      toast.success("Call unpaused");
    } catch {
      toast.error("Failed to unpause call");
    }
  },
}));

export default useServiceCallsStore;
